import { FormEvent, useEffect, useState } from 'react';
import type { InvitationCodeInput, PlatformInvitationCode, PlatformPlanOption } from '@/services/api/platform.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

interface InvitationCodeFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plans: PlatformPlanOption[];
  /** Code being edited; omitted when creating. */
  initial?: PlatformInvitationCode | null;
  onSubmit: (input: InvitationCodeInput) => Promise<void>;
}

// <input type="date"> works in local days; a code stays usable through the
// whole day it expires on.
const toDateInput = (iso: string | null) => (iso ? iso.slice(0, 10) : '');
const fromDateInput = (value: string) => (value ? new Date(`${value}T23:59:59`).toISOString() : null);

export function InvitationCodeFormDialog({ open, onOpenChange, plans, initial, onSubmit }: InvitationCodeFormDialogProps) {
  const editing = Boolean(initial);
  // The backend refuses renaming a code once organizations signed up with it.
  const codeLocked = Boolean(initial && initial.signupsCount > 0);

  const [code, setCode] = useState('');
  const [stripeCodeId, setStripeCodeId] = useState('');
  const [planId, setPlanId] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [maxSignups, setMaxSignups] = useState('');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCode(initial?.code ?? '');
    setStripeCodeId(initial?.stripeCodeId ?? '');
    setPlanId(initial ? String(initial.subscriptionPlanId) : '');
    setExpiresOn(toDateInput(initial?.expiresAt ?? null));
    setMaxSignups(initial?.maxSignups ? String(initial.maxSignups) : '');
    setNotes(initial?.notes ?? '');
    setErrors([]);
  }, [open, initial]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!planId) {
      setErrors(['Pick the plan the code grants']);
      return;
    }

    setSubmitting(true);
    setErrors([]);
    try {
      await onSubmit({
        ...(codeLocked ? {} : { code: code.trim().toUpperCase() }),
        stripeCodeId: stripeCodeId.trim(),
        subscriptionPlanId: Number(planId),
        expiresAt: fromDateInput(expiresOn),
        maxSignups: maxSignups ? Number(maxSignups) : null,
        notes: notes.trim() || null,
      });
      onOpenChange(false);
    } catch (err: any) {
      const data = err?.response?.data;
      setErrors(Array.isArray(data?.errors) && data.errors.length ? data.errors : [data?.error || 'Could not save the code']);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? `Edit ${initial?.code}` : 'New invitation code'}</DialogTitle>
          <DialogDescription>
            The Stripe coupon must be 100% off: no card is collected at signup. Saving runs the validity check.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4" aria-label="Invitation code">
          {errors.length > 0 && (
            <div role="alert" className="rounded bg-danger-soft px-3 py-2 text-sm text-danger-text">
              {errors.map((message) => <p key={message}>{message}</p>)}
            </div>
          )}

          <div className="space-y-1">
            <Label htmlFor="ic-code">Code</Label>
            <Input
              id="ic-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="SLOTY-WELCOME"
              disabled={codeLocked}
              required={!codeLocked}
            />
            {codeLocked && <p className="text-xs text-ink-subtle">Locked: organizations already signed up with it.</p>}
          </div>

          <div className="space-y-1">
            <Label htmlFor="ic-stripe">Stripe coupon id</Label>
            <Input
              id="ic-stripe"
              value={stripeCodeId}
              onChange={(e) => setStripeCodeId(e.target.value)}
              placeholder="INVITE3MFREE"
              required
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="ic-plan">Plan granted</Label>
            <Select value={planId} onValueChange={setPlanId}>
              <SelectTrigger id="ic-plan" aria-label="Plan granted">
                <SelectValue placeholder="Choose a plan" />
              </SelectTrigger>
              <SelectContent>
                {plans.map((plan) => (
                  <SelectItem key={plan.id} value={String(plan.id)}>
                    {plan.name} ({plan.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="ic-expires">Expires on (optional)</Label>
              <Input id="ic-expires" type="date" value={expiresOn} onChange={(e) => setExpiresOn(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ic-max">Max signups (optional)</Label>
              <Input
                id="ic-max"
                type="number"
                min={1}
                value={maxSignups}
                onChange={(e) => setMaxSignups(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="ic-notes">Notes</Label>
            <Textarea id="ic-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? 'Saving…' : editing ? 'Save' : 'Create code'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
